import type { TooltipAnchor, TooltipPlacement } from "@stores/useTooltip";

/**
 * Where the one tooltip pill goes. Pure maths, deliberately outside `Tooltip.vue`: every rule here is
 * "does the pill still fit on screen", which is exactly the kind of thing that regresses silently and
 * can only be caught by a test that does not need a browser.
 *
 * Two families of placement:
 *  - `cursor` trails the pointer below-right; `cursor-top` trails it but sits above, clear of the
 *    cursor glyph (the arrow and the not-allowed badge extend downward from the hotspot).
 *  - `top` / `right` / `bottom` are anchored to the trigger's rect and must be kept inside the
 *    viewport by hand. `html { overflow-x: hidden }` clips anything that runs past the right edge, so
 *    an anchored pill on a right-most trigger loses its tail unless the position is clamped.
 */

/** Gap between the pointer and the pill, and the flip threshold for a cursor-followed tooltip. */
const CURSOR_OFFSET = 10;
/** Smallest gap the pill keeps from a viewport edge when following the cursor. */
const VIEWPORT_MARGIN = 10;
/** Smallest gap an *anchored* pill keeps from a viewport edge. */
const ANCHOR_EDGE_MARGIN = 8;
/** Gap between the trigger and an anchored pill. */
const ANCHOR_GAP = 4;
/** Bottom-anchored pills sit a little lower: they hang under toolbar buttons with their own padding. */
const ANCHOR_GAP_BELOW = 8;
/** Never narrow a pill below this, however cramped the viewport is. */
const MIN_PILL_WIDTH = 120;
/** Total horizontal breathing room an anchored pill leaves against the viewport when bounding its width. */
const ANCHOR_WIDTH_INSET = 24;
/** Used until the pill has been measured. Matches `max-w-xs` (20rem), the `hint` variant's cap. */
const FALLBACK_PILL_WIDTH = 320;

export type TooltipPositionInput = {
  placement: TooltipPlacement;
  /** The trigger's rect, or `null` when the caller gave none — then the cursor maths is used. */
  anchor: TooltipAnchor | null;
  /** The measured pill. Zeroes mean "not measured yet"; the maths falls back rather than guessing 0. */
  pill: { width: number; height: number };
  cursor: { x: number; y: number };
  viewportWidth: number;
};

/** A plain style object — the pill is positioned in pixels, so this is bound with `:style`. */
export type TooltipPositionStyle = {
  top: string;
  left: string;
  transform?: string;
  maxWidth?: string;
};

/**
 * Horizontal placement shared by the two centred placements (`top` and `bottom`).
 *
 * `left` is the pill's *centre* because of the `translateX(-50%)`, so it is clamped by half the
 * measured width — clamping by the full width would push every centred pill a half-width off target.
 */
function centredHorizontally(anchor: TooltipAnchor, width: number, viewportWidth: number) {
  const half = width / 2;
  const centre = (anchor.left + anchor.right) / 2;
  const clamped =
    viewportWidth > 0 ? Math.min(Math.max(centre, half + ANCHOR_EDGE_MARGIN), viewportWidth - half - ANCHOR_EDGE_MARGIN) : centre;

  return {
    left: `${clamped}px`,
    maxWidth: `${Math.max(MIN_PILL_WIDTH, viewportWidth - ANCHOR_WIDTH_INSET)}px`,
  };
}

export function resolveTooltipPosition({
  placement,
  anchor,
  pill,
  cursor,
  viewportWidth,
}: TooltipPositionInput): TooltipPositionStyle {
  const width = pill.width || FALLBACK_PILL_WIDTH;
  const height = pill.height || 0;

  // ── Anchored to an element ──────────────────────────────────────────────────
  // Only reachable when a caller supplied the rect; without one we fall through to the cursor maths
  // rather than pinning the pill to 0,0.
  if (anchor) {
    // Centred under the trigger.
    if (placement === "bottom") {
      return {
        top: `${anchor.bottom + ANCHOR_GAP_BELOW}px`,
        ...centredHorizontally(anchor, width, viewportWidth),
        transform: "translateX(-50%)",
      };
    }

    // To the right of the trigger, centred on its vertical midpoint. Used by the collapsed sidebar,
    // i.e. always against the left edge, so it needs no horizontal flip — only a width bound.
    if (placement === "right") {
      return {
        top: `${(anchor.top + anchor.bottom) / 2}px`,
        left: `${anchor.right + ANCHOR_GAP}px`,
        transform: "translateY(-50%)",
        maxWidth: `${Math.max(MIN_PILL_WIDTH, viewportWidth - anchor.right - ANCHOR_WIDTH_INSET)}px`,
      };
    }

    // Centred above the trigger — and clamped exactly like `bottom`. Without the clamp a trigger at
    // the right end of a row (the transport list's Add and delete controls, the read-only hints on
    // the mapping pages) pushed the pill past the right edge, where it was cut off mid-word.
    if (placement === "top") {
      const horizontal = centredHorizontally(anchor, width, viewportWidth);
      // Flip below when there is no room above, so a trigger in a page's first row still explains
      // itself instead of rendering off the top of the viewport.
      const fitsAbove = anchor.top - ANCHOR_GAP - height >= ANCHOR_EDGE_MARGIN;

      return fitsAbove
        ? { top: `${anchor.top - ANCHOR_GAP}px`, ...horizontal, transform: "translate(-50%, -100%)" }
        : { top: `${anchor.bottom + ANCHOR_GAP}px`, ...horizontal, transform: "translateX(-50%)" };
    }
  }

  // ── Following the cursor ────────────────────────────────────────────────────
  const clampLeft = (value: number) => {
    let left = value;
    if (viewportWidth > 0 && left + width + VIEWPORT_MARGIN > viewportWidth) left = viewportWidth - width - VIEWPORT_MARGIN;
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    return left;
  };

  // Centred horizontally on the cursor, sitting above it (flips below when too near the top).
  // `cursor-top` is the placement callers pick for this; anchor-less `top` falls through to the same
  // maths rather than pinning the pill to 0,0.
  if (placement === "top" || placement === "cursor-top") {
    const left = clampLeft(cursor.x - width / 2);
    const above = cursor.y - height - CURSOR_OFFSET;
    const top = above < VIEWPORT_MARGIN ? cursor.y + CURSOR_OFFSET : above;
    return { top: `${top}px`, left: `${left}px` };
  }

  // Default: offset to the bottom-right of the cursor, flipping left on right overflow.
  const preferredLeft = cursor.x + CURSOR_OFFSET;
  const overflowsRight = viewportWidth > 0 && preferredLeft + width + VIEWPORT_MARGIN > viewportWidth;
  const left = overflowsRight ? Math.max(VIEWPORT_MARGIN, cursor.x - CURSOR_OFFSET - width) : preferredLeft;

  return { top: `${cursor.y + CURSOR_OFFSET}px`, left: `${left}px` };
}

export default resolveTooltipPosition;
